import { useState } from "react";
import { useDeleteProductById, useGetProductDataV2 } from "../../../../api/product";
import { CalculationFilterStatus } from "../../../../config/enums/ProductType.enum";
import { ROLES } from "../../../../config/permission-maps";
import { errorView } from "../../../../helpers/ErrorHandler";
import { getUserRolesInLocal } from "../../../../utils/cacheStorage";
import { ProductRowMapper } from "../services/ProductRowMapper";
import { ProductListViewModel, ProductRowViewModel } from "../types/productListView.types";

const DEFAULT_PAGE_SIZE = 10;

/**
 * Owns every piece of state, data fetching and mutation handling that the
 * legacy `ProductList` kept inline (see
 * src/screens/product/productList/ProductList.tsx). Presentational
 * components consume the `ProductListViewModel` this hook returns instead
 * of importing `api/product` themselves — the same dependency-inversion
 * split used for `useIngredientListViewModel` in
 * src/screens/lcaIngredients/ingredientList/hooks/.
 */
export const useProductListViewModel = (): ProductListViewModel => {
  const [productId, setProductId] = useState<string>("");
  const [visibleRegisterModal, setVisibleRegisterModal] = useState(false);
  const [visibleDetailsModal, setVisibleDetailsModal] = useState(false);
  const [visibleEditModal, setVisibleEditModal] = useState(false);
  const [visibleDeleteConfirm, setVisibleDeleteConfirm] = useState(false);
  const [visibleRegModal, setVisibleRegModal] = useState(false);
  const [dataToPass, setDataToPass] = useState<any>(null);
  const [deleteData, setDeleteData] = useState<ProductRowViewModel | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [searchKey, setSearchKey] = useState("");
  const [calculateStatus, setCalculateStatus] = useState<CalculationFilterStatus>(
    CalculationFilterStatus.NONE
  );
  const [orderBy, setOrderBy] = useState("createdAtDesc");

  const localUserRole = getUserRolesInLocal();
  const isUserSupplier = localUserRole?.[0] === ROLES.supplier;

  const {
    data,
    isLoading: isRowsLoading,
    refetch: refetchProducts
  } = useGetProductDataV2({
    product_status: "ACTIVE",
    pageNumber,
    pageSize,
    calculateStatus,
    searchKey,
    orderBy
  });

  const { isLoading: isDeleteLoading, mutate: deleteProduct } = useDeleteProductById();

  const refetchWithFilters = (status: CalculationFilterStatus, order: string) => {
    setCalculateStatus(status);
    setOrderBy(order);
  };

  const openDetailsModal = (row: ProductRowViewModel) => {
    setProductId(row.product_id);
    setVisibleDetailsModal(true);
  };

  const openEditModal = (row: ProductRowViewModel) => {
    setProductId(row.product_id);
    setVisibleEditModal(true);
  };

  const openDeleteConfirm = (row: ProductRowViewModel) => {
    setDeleteData(row);
    setVisibleDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setVisibleDeleteConfirm(false);
    setDeleteData(null);
  };

  const confirmDelete = () => {
    if (deleteData?.hasSalesUnits) {
      errorView("This Product has Sales Units, it cannot be deleted.");
      setVisibleDeleteConfirm(false);
      return;
    }
    if (deleteData?.product_id) {
      deleteProduct(deleteData.product_id, {
        onSuccess: () => refetchProducts()
      });
    }
    setVisibleDeleteConfirm(false);
    setDeleteData(null);
  };

  const navigateDetailsToEdit = () => {
    setVisibleDetailsModal(false);
    setVisibleEditModal(true);
  };

  // Mirrors the legacy `handleNavigationRegister` exactly (see
  // src/screens/product/productList/ProductList.tsx): saving a product can
  // ask the caller to immediately open the ingredient register flow
  // (`enableNavigate`) and/or jump straight into editing the new product
  // (`enableEditNavigation`).
  const handleNavigationRegister = (status: boolean, regData: any) => {
    if (!status) {
      return;
    }
    refetchProducts();
    setVisibleRegisterModal(false);
    if (regData?.enableNavigate || false) {
      setDataToPass(regData.productData);
    }
    if (regData?.enableEditNavigation || false) {
      setProductId(regData.productData.product_id);
      setVisibleEditModal(true);
    }
    setVisibleRegModal(regData?.enableNavigate || false);
  };

  return {
    rows: ProductRowMapper.toRowViewModels(data?.result, isUserSupplier),
    isRowsLoading,
    isMutationInFlight: isDeleteLoading,
    totalCount: data?.total || 0,
    totalPages: Math.ceil((data?.total || 0) / pageSize) || 1,
    pageNumber,
    searchKey,
    isUserSupplier,

    setSearchKey,
    handlePagination: setPageNumber,
    refetchWithFilters,

    productId,
    dataToPass,

    visibleRegisterModal,
    visibleDetailsModal,
    visibleEditModal,
    visibleDeleteConfirm,
    visibleRegModal,

    openRegisterModal: () => setVisibleRegisterModal(true),
    handleNavigationRegister,
    openDetailsModal,
    closeDetailsModal: () => setVisibleDetailsModal(false),
    openEditModal,
    closeEditModal: (status: boolean) => {
      // Mirrors the legacy close handler exactly: it only reacts when the
      // child reports `true` (save succeeded) — see
      // src/screens/product/productList/ProductList.tsx.
      if (status) {
        refetchProducts();
        setVisibleEditModal(false);
      }
    },
    openDeleteConfirm,
    closeDeleteConfirm,
    confirmDelete,
    closeIngredientRegisterModal: () => {
      setDataToPass(null);
      setVisibleRegModal(false);
    },

    navigateDetailsToEdit,

    refetchProducts
  };
};
